import { useEffect, useRef, useState } from "react";
import {
  ON_ROUTE_CONFIRMATION_POINTS,
  ROUTE_DEVIATION_HYSTERESIS_METERS,
  ROUTE_LEAVE_DRAW_METERS,
} from "./mapConfig";
import {
  applyTrailPoint,
  createTrailMachine,
  getRenderableSegments,
  normalizeGpsPoint,
} from "./offRouteTrail";

const LIVE_BUFFER_MAX = 2000;

function eventKey(event) {
  if (!event) return null;
  if (event.eventId) return `id:${event.eventId}`;
  if (!event.type) return null;
  return `${event.type}:${event.timestamp || ""}:${event.latitude || ""}:${event.longitude || ""}`;
}

export function useOffRouteTrail({
  deviceId,
  tripStartISO,
  fullPath,
  position,
  routeDeviationEvent,
  routeDeviation,
  tripGpsHistory,
}) {
  const [offRouteSegments, setOffRouteSegments] = useState([]);
  const [isOffRoute, setIsOffRoute] = useState(false);

  const machineRef = useRef(null);
  const bufferRef = useRef([]);
  const hydratedKeyRef = useRef(null);
  const hydratedWithHistoryRef = useRef(false);
  const lastEventKeyRef = useRef(null);
  const lastLivePointRef = useRef(null);

  const trailKey = `${deviceId || ""}:${tripStartISO || ""}`;

  const publish = () => {
    const machine = machineRef.current;
    if (!machine) {
      setOffRouteSegments([]);
      setIsOffRoute(false);
      return;
    }
    setIsOffRoute(machine.state === "OFF_ROUTE");
    setOffRouteSegments(getRenderableSegments(machine));
  };

  const resetMachine = () => {
    machineRef.current = createTrailMachine({
      thresholdMeters: ROUTE_LEAVE_DRAW_METERS,
      confirmOffPoints: 1,
      confirmOnPoints: ON_ROUTE_CONFIRMATION_POINTS,
      hysteresisMeters: ROUTE_DEVIATION_HYSTERESIS_METERS,
    });
    lastLivePointRef.current = null;
  };

  useEffect(() => {
    bufferRef.current = [];
    hydratedKeyRef.current = null;
    hydratedWithHistoryRef.current = false;
    lastEventKeyRef.current = null;
    resetMachine();
    publish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trailKey]);

  useEffect(() => {
    const point = normalizeGpsPoint(position);
    if (!point) return;

    bufferRef.current = [...bufferRef.current, point].slice(-LIVE_BUFFER_MAX);

    const nextEventKey = eventKey(routeDeviationEvent);
    const isNewEvent =
      Boolean(nextEventKey) && nextEventKey !== lastEventKeyRef.current;

    if (hydratedKeyRef.current !== trailKey) {
      if (isNewEvent) lastEventKeyRef.current = nextEventKey;
      return;
    }

    const samePoint =
      lastLivePointRef.current &&
      lastLivePointRef.current.lat === point.lat &&
      lastLivePointRef.current.lng === point.lng &&
      lastLivePointRef.current.timestamp === point.timestamp;
    if (samePoint && !isNewEvent) return;
    lastLivePointRef.current = point;

    if (!machineRef.current) resetMachine();
    if (isNewEvent) lastEventKeyRef.current = nextEventKey;

    applyTrailPoint(machineRef.current, point, { fullPath });
    publish();
  }, [position, fullPath, routeDeviationEvent, routeDeviation, trailKey]);

  useEffect(() => {
    if (!deviceId || !Array.isArray(fullPath) || fullPath.length < 2) return;

    const waitingForHistory = Boolean(tripStartISO) && tripGpsHistory == null;
    if (waitingForHistory) return;

    const history = Array.isArray(tripGpsHistory) ? tripGpsHistory : [];
    const alreadyHydrated = hydratedKeyRef.current === trailKey;
    const historyJustArrived =
      alreadyHydrated && !hydratedWithHistoryRef.current && history.length > 0;
    if (alreadyHydrated && !historyJustArrived) return;

    resetMachine();
    let lastHistoryTs = 0;
    for (const raw of history) {
      const normalized = normalizeGpsPoint(raw);
      if (normalized?.timestamp) {
        lastHistoryTs = Math.max(lastHistoryTs, normalized.timestamp);
      }
      applyTrailPoint(machineRef.current, raw, { fullPath });
    }

    for (const point of bufferRef.current) {
      if (lastHistoryTs && point.timestamp && point.timestamp <= lastHistoryTs) {
        continue;
      }
      applyTrailPoint(machineRef.current, point, { fullPath });
    }

    hydratedKeyRef.current = trailKey;
    hydratedWithHistoryRef.current = history.length > 0;
    publish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, trailKey, fullPath, tripGpsHistory, tripStartISO]);

  return { offRouteSegments, isOffRoute };
}
